import { Box, Typography, makeStyles } from '@material-ui/core'
import { mdiAccountGroupOutline } from '@mdi/js'
import Icon from '@mdi/react'
import React from 'react'
import { useSelector } from 'react-redux'

import colors from '../../../../assets/colors'

import StatusChip from './StatusChip'
import {
  ADDON_STATUS_ACTIVE,
  ADDON_STATUS_PENDING,
  accessQuantity,
  formatAddonDate,
  hasScheduledReduction,
} from './addonHelpers'

// o teto de usuários (max_users) que a API devolve já vem somado:
// vagas do plano contratado + quantidade do adicional "Usuário extra" ATIVO.
// Como o cliente não sabe dizer o que é do plano e o que foi ele que contratou,
// separamos as duas origens aqui usando os add-ons contratados do store.
const EXTRA_USER_SLUG = 'usuario-extra'

const useStyles = makeStyles({
  wrapper: {
    border: `1px solid ${colors.graylight}`,
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 700,
    fontSize: '14px',
    color: colors.primary,
  },
  counter: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
  },
  track: {
    width: '100%',
    height: '6px',
    borderRadius: '4px',
    backgroundColor: colors.lightGray,
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.4s ease',
  },
  sectionTitle: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: colors.textPrimary,
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '3px 0',
  },
  rowLabel: {
    fontSize: '12px',
    color: colors.textGray,
  },
  rowValue: {
    fontSize: '12px',
    fontWeight: 700,
    color: colors.primary,
    whiteSpace: 'nowrap',
  },
  totalRow: {
    borderTop: `1px solid ${colors.graylight}`,
    marginTop: '2px',
    paddingTop: '6px',
  },
  hint: {
    fontSize: '11px',
    color: colors.textGray,
  },
  caption: {
    fontSize: '11px',
    color: colors.textGray,
  },
})

const Row = ({ label, hint, value, valueColor, total }) => {
  const classes = useStyles()

  return (
    <Box className={`${classes.row} ${total ? classes.totalRow : ''}`}>
      <Box>
        <Typography
          className={classes.rowLabel}
          style={total ? { fontWeight: 700, color: colors.primary } : undefined}
        >
          {label}
        </Typography>
        {hint && <Typography className={classes.hint}>{hint}</Typography>}
      </Box>
      <Typography
        className={classes.rowValue}
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </Typography>
    </Box>
  )
}

const plural = (qty, singular, pluralWord) =>
  `${qty} ${qty === 1 ? singular : pluralWord}`

// consumo do limite: apenas usuários ativos ocupam vaga
// (mesma regra da listagem de clientes: active_clients / max_users)
const UsersUsage = () => {
  const classes = useStyles()
  const subscriptionStatus = useSelector(
    state => state.subscription.subscriptionStatus
  )
  const contractedAddons = useSelector(
    state => state.subscription.contractedAddons
  )
  // sem a lista de contratados não há como dizer quais vagas vêm do plano e
  // quais vêm do adicional; até ela chegar, a origem das vagas fica oculta
  const addonsLoaded = useSelector(state => state.subscription.myAddonsLoaded)

  const {
    active_users: activeUsers,
    inactive_users: inactiveUsers,
    total_users: totalUsers,
    max_users: maxUsers,
  } = subscriptionStatus || {}

  const active = Number(activeUsers || 0)
  const inactive = Number(inactiveUsers || 0)
  const total = Number(totalUsers || active + inactive)
  const limit = Number(maxUsers || 0)

  if (!limit) return null

  const extraContracts = (contractedAddons || []).filter(
    item => (item.addon_service || {}).slug === EXTRA_USER_SLUG
  )

  // as vagas seguem a quantidade de ACESSO (quantity): é o que o cliente pode
  // usar agora, ainda que a próxima fatura já vá cobrar menos
  const sumQuantity = status =>
    extraContracts
      .filter(item => item.status === status)
      .reduce((acc, item) => acc + accessQuantity(item), 0)

  // só os extras ATIVOS entram no max_users; os pendentes são avisados à parte
  // para o cliente entender por que as vagas que contratou ainda não apareceram
  const extraActive = sumQuantity(ADDON_STATUS_ACTIVE)
  const extraPending = sumQuantity(ADDON_STATUS_PENDING)

  const contractedByTeam = extraContracts.some(
    item =>
      item.status === ADDON_STATUS_ACTIVE && item.contracted_by === 'admin'
  )

  // redução de quantidade agendada: as vagas de hoje seguem liberadas até o fim
  // do ciclo já pago, e só depois caem para a quantidade que será cobrada
  const scheduledReduction = extraContracts.find(
    item => item.status === ADDON_STATUS_ACTIVE && hasScheduledReduction(item)
  )

  const planIncluded = Math.max(0, limit - extraActive)

  const available = Math.max(0, limit - active)
  const percent = Math.min(100, Math.round((active / limit) * 100))

  const reachedLimit = active >= limit
  const nearLimit = !reachedLimit && available <= 1

  let tone = 'success'
  if (reachedLimit) tone = 'error'
  else if (nearLimit) tone = 'info'

  const toneColor = {
    success: colors.sucess,
    info: colors.info,
    error: colors.error,
  }[tone]

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          <Icon path={mdiAccountGroupOutline} size={0.8} />
          Usuários
        </Typography>
        {reachedLimit && <StatusChip label="Limite atingido" tone="error" />}
        {nearLimit && <StatusChip label="Perto do limite" tone="info" />}
      </Box>

      <Box>
        <Box className={classes.counter}>
          <Typography
            style={{ fontSize: '20px', fontWeight: 'bold', color: toneColor }}
          >
            {active}
          </Typography>
          <Typography style={{ fontSize: '12px', color: colors.textGray }}>
            de {limit} vagas em uso
          </Typography>
        </Box>

        <Box className={classes.track} style={{ marginTop: '6px' }}>
          <Box
            className={classes.fill}
            style={{ width: `${percent}%`, backgroundColor: toneColor }}
          />
        </Box>
      </Box>

      <Box>
        <Typography className={classes.sectionTitle}>
          De onde vêm suas vagas
        </Typography>

        {addonsLoaded && (
          <>
            <Row
              label="Inclusas no plano"
              value={plural(planIncluded, 'vaga', 'vagas')}
            />

            <Row
              label='Adicional "Usuário extra"'
              hint={
                extraActive > 0
                  ? `${
                      contractedByTeam
                        ? 'contratado pela nossa equipe'
                        : 'contratado por você'
                    } · cobrado por unidade`
                  : null
              }
              value={
                extraActive > 0
                  ? `+ ${plural(extraActive, 'vaga', 'vagas')}`
                  : 'não contratado'
              }
              valueColor={extraActive > 0 ? colors.sucess : colors.textPrimary}
            />
          </>
        )}
      </Box>

      {scheduledReduction && (
        <Typography className={classes.caption}>
          {`Você reduziu o adicional para ${
            scheduledReduction.scheduled_quantity
          } ${
            scheduledReduction.scheduled_quantity === 1 ? 'vaga' : 'vagas'
          }: as vagas atuais seguem liberadas${
            formatAddonDate(scheduledReduction.quantity_change_at)
              ? ` até ${formatAddonDate(scheduledReduction.quantity_change_at)}`
              : ' até o fim do ciclo já pago'
          }, e a próxima fatura já vem com a quantidade reduzida.`}
        </Typography>
      )}

      {extraPending > 0 && (
        <Typography className={classes.caption}>
          {extraPending === 1
            ? '+ 1 vaga extra contratada, ainda aguardando liberação.'
            : `+ ${extraPending} vagas extras contratadas, ainda aguardando liberação.`}
        </Typography>
      )}

      <Typography className={classes.caption}>
        {total} cadastrado(s) · {active} ativo(s) (ocupam vaga) · {inactive}{' '}
        inativo(s) (não ocupam)
      </Typography>

      <Typography className={classes.caption} style={{ color: toneColor }}>
        {reachedLimit
          ? 'Todas as vagas estão em uso. Para liberar novos acessos, contrate o adicional "Usuário extra" abaixo informando quantas vagas precisa — ou inative um usuário que não usa mais o sistema.'
          : `Você ainda pode ativar ${plural(
              available,
              'usuário',
              'usuários'
            )} sem contratar nada. Precisando de mais vagas, ajuste a quantidade do adicional "Usuário extra" abaixo.`}
      </Typography>
    </Box>
  )
}

export default UsersUsage
